'use client'

import Heading from '@lyra/core/components/Heading'
import TabsHorizontal from '@lyra/web/components/layout/TabsHorizontal'
import DepthChart from '@lyra/web/containers/common/DepthChart'
import Orderbook from '@lyra/web/containers/common/Orderbook'
import TradeFeed from '@lyra/web/containers/common/TradeFeed'
import FundingChart from '@lyra/web/containers/trade/FundingChart'
import useOrderQueryParamsForInstrument from '@lyra/web/hooks/useOrderQueryParamsForInstrument'
import { getPerpInstrumentName } from '@lyra/web/utils/instruments'
import { useCallback, useState } from 'react'
import { XStack, YStack } from 'tamagui'

import { PERPS_ORDERBOOK_WIDTH_DESKTOP } from '../../../constants/layout'
import { MarketId } from '../../../constants/markets'
import TradingViewChart from '../../../containers/common/TradingViewChart'

type Props = {
  marketId: MarketId
}

export default function PerpsPageHelperDesktop({ marketId }: Props) {
  const instrumentName = getPerpInstrumentName(marketId)

  const [chartTab, setChartTab] = useState<'price' | 'depth' | 'funding'>('price')

  const chartTabs = [
    {
      onPress: () => setChartTab('price'),
      isSelected: chartTab === 'price',
      name: 'Price',
    },
    {
      onPress: () => setChartTab('depth'),
      isSelected: chartTab === 'depth',
      name: 'Depth',
    },
    {
      onPress: () => setChartTab('funding'),
      isSelected: chartTab === 'funding',
      name: 'Funding',
    },
  ]

  const [quoteParams, onChangeQuoteParams] = useOrderQueryParamsForInstrument(instrumentName)
  const limitPrice = +quoteParams.limit_price

  const handlePressTick = useCallback(
    (price: number) => {
      onChangeQuoteParams((params) => ({
        ...params,
        limit_price: price.toString(),
      }))
    },
    [onChangeQuoteParams]
  )

  return (
    <XStack flexGrow={1} flexShrink={1}>
      <YStack
        borderRightWidth={1}
        borderTopWidth={1}
        borderColor="$hairline"
        flexGrow={1}
        flexShrink={1}
        height="100%"
      >
        <TabsHorizontal pages={chartTabs} borderBottomColor="$hairline" borderBottomWidth={1} />
        <YStack flexGrow={1} flexShrink={1}>
          {chartTab === 'price' ? (
            <TradingViewChart currencyId={marketId} width="100%" height="100%" />
          ) : chartTab === 'funding' ? (
            <FundingChart instrumentName={instrumentName} height="100%" />
          ) : (
            <DepthChart instrumentName={instrumentName} height="100%" />
          )}
        </YStack>
      </YStack>
      <YStack
        borderColor="$hairline"
        borderTopWidth={1}
        height="100%"
        width={PERPS_ORDERBOOK_WIDTH_DESKTOP}
      >
        <YStack height="50%" paddingTop="$2.5" gap="$1">
          <XStack paddingHorizontal="$3">
            <Heading>Book</Heading>
          </XStack>
          <Orderbook
            flexGrow={1}
            flexShrink={1}
            numTicks={10}
            paddingHorizontal="$1"
            instrumentName={instrumentName}
            selectedLimitPrice={limitPrice}
            onPressTick={handlePressTick}
          />
        </YStack>
        <YStack height="50%" borderColor="$hairline" borderTopWidth={1} paddingTop="$2.5" gap="$1">
          <XStack paddingHorizontal="$3">
            <Heading>Trades</Heading>
          </XStack>
          <TradeFeed
            pageSize={8}
            paddingHorizontal="$1"
            flexGrow={1}
            flexShrink={1}
            instrumentName={instrumentName}
          />
        </YStack>
      </YStack>
    </XStack>
  )
}
